import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Button, Container, Empty, Heading, Skeleton, Title } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import { ChevronLeftIcon } from '@treams/treams-icons';
import DepartmentDetails from './DepartmentDetails';
import DepartmentToolbar from './DepartmentToolbar';
import GroupMember from './GroupMember';

const Background = styled(Box)`
	flex-grow: 1;
	flex-shrink: 1;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const Grid = styled(Box)`
	display: grid;
	max-width: 560px;
	grid-template-columns: repeat(auto-fit, 180px);
	gap: 10px;
`;

export default function Group(props: { id: string }) {
	const { t } = useTranslation();

	const query = graphql.useGroupQuery({ variables: { id: props.id, first: 10000 } });

	return (
		<Background>
			<Container py={4} wide={1080}>
				<Flex mb={3}>
					<Button
						icon={<ChevronLeftIcon />}
						title={t('common.back')}
						type="secondary"
						link="/settings/organization/departments"
					/>
				</Flex>
				<Flex alignItems="center">
					<Title flexGrow={1} flexShrink={1}>
						{t('settings.departments.department.title')}
					</Title>
					{query.data ? (
						<DepartmentToolbar group={query.data.group as graphql.Group} />
					) : (
						<Button.Skeleton />
					)}
				</Flex>
				<Flex mb={4}>
					<Box flexBasis={0} flexGrow={1} flexShrink={1} mr={2}>
						{query.data ? (
							<DepartmentDetails group={query.data.group as graphql.Group} />
						) : (
							<DepartmentDetails.Skeleton />
						)}
					</Box>
					<Box flexBasis={0} flexGrow={1} flexShrink={1} ml={2}>
						<Flex height={46} alignItems="center" mb={3}>
							<Heading>{t('settings.departments.department.managers.title')}:</Heading>
						</Flex>
						{query.data ? (
							query.data.group.managers.edges.length === 0 ? (
								<Box width={180 * 3 + 20}>
									<Skeleton.Empty
										contents={
											<Empty
												small
												description={t('settings.departments.department.managers.empty')}
											/>
										}
									>
										<Grid>
											<Skeleton.Repeat count={3}>
												<GroupMember.Skeleton />
											</Skeleton.Repeat>
										</Grid>
									</Skeleton.Empty>
								</Box>
							) : (
								<Grid>
									{query.data.group.managers.edges.map((edge) => (
										<GroupMember
											key={edge.node.id}
											user={edge.node as graphql.User}
											role={edge.role as graphql.Role}
										/>
									))}
								</Grid>
							)
						) : (
							<Grid>
								<Skeleton.Repeat count={3}>
									<GroupMember.Skeleton />
								</Skeleton.Repeat>
							</Grid>
						)}
					</Box>
				</Flex>

				<Box mb={4}>
					<Heading mb={3}>{t('settings.departments.department.members.title')}:</Heading>
					{query.data ? (
						query.data.group.members.edges.length === 0 ? (
							<Box width={180 * 3 + 20}>
								<Skeleton.Empty
									contents={
										<Empty small description={t('settings.departments.department.members.empty')} />
									}
								>
									<Grid>
										<Skeleton.Repeat count={3}>
											<GroupMember.Skeleton />
										</Skeleton.Repeat>
									</Grid>
								</Skeleton.Empty>
							</Box>
						) : (
							<Grid>
								{_.sortBy(
									query.data?.group.members.edges,
									(edge) => `${edge.node.firstName} ${edge.node.lastName}`,
								).map((edge) => (
									<GroupMember
										key={edge.node.id}
										user={edge.node as graphql.User}
										role={
											{
												id: 'group-member',
												type: graphql.RoleType.Member,
											} as graphql.Role
										}
									/>
								))}
							</Grid>
						)
					) : (
						<Grid>
							<Skeleton.Repeat count={3}>
								<GroupMember.Skeleton />
							</Skeleton.Repeat>
						</Grid>
					)}
				</Box>
			</Container>
		</Background>
	);
}
